import { ServiceRequestDetail } from "@features/cx/services/types";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { CustomTooltip } from "@shared/components/CustomTooltip";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Checkbox } from "@shared/ui/checkbox";
import { Badge } from "@shared/ui/badge";
import {
  AlertCircle,
  AlertTriangle,
  Building,
  Calendar,
  CalendarIcon,
  CheckCircle,
  CheckSquare,
  Clock,
  Cog,
  DollarSign,
  FileText,
  Info,
  MapPinIcon,
  Pause,
  Printer,
  Settings,
  Wrench,
  X,
  Zap,
} from "lucide-react";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { PaymentDetailsModal } from "@features/cx/paymentsv2/components/Detail/PaymentDetail";
import { formatTerritoryAddress } from "@shared/utils/address";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RecentActivityCard } from "@features/cx/shared/components/RecentActivityCard";
import { useServiceOrders } from "@features/cx/shared/hooks/useServiceOrder";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { cn } from "@shared/lib/utils";

interface ClosureRequirements {
  customerNotification: boolean;
  serviceCompletion: boolean;
  qualityCheck: boolean;
  documentationUpload: boolean;
}

interface ServiceDetailsTabProps {
  request: ServiceRequestDetail;
  remoteUtilityId: number;
  onResolve?: (closureRequirements: any) => void;
  onReject?: (requestId: string) => void;
  closureRequirements?: any;
  setClosureRequirements?: any;
  isResolving?: boolean;
}

const ServiceDetailsTab: React.FC<ServiceDetailsTabProps> = ({
  request,
  remoteUtilityId,
  onResolve,
  onReject,
  closureRequirements,
  setClosureRequirements,
  isResolving,
}) => {
  const navigate = useNavigate();
  const currency = useCurrency();
  const isServiceCompleted =
    String(request?.statusDisplay || "").toLowerCase().trim() === "completed" ||
    String(request?.status || "").toLowerCase().trim() === "completed" ||
    request?.status === 5;

  // Update state when request data loads
  useEffect(() => {
    const closure = request?.additionalData?.closureRequirements;

    if (closure) {
      setClosureRequirements({
        customerNotification: closure.customerNotification ?? false,
        serviceCompletion: closure.serviceCompletionVerification ?? false,
        qualityCheck: closure.qualityCheckPending ?? false,
        documentationUpload: closure.documentationUpload ?? false,
      });
    }
  }, [request?.additionalData]);

  const handleClosureRequirementChange = (
    requirement: keyof ClosureRequirements,
    checked: boolean,
  ) => {
    setClosureRequirements((prev) => ({
      ...prev,
      [requirement]: checked,
    }));
  };

  const handleResolveRequest = async () => {
    if (onResolve) {
      onResolve(closureRequirements);
    }
  };

  const handleRejectRequest = async () => {
    if (onReject) {
      onReject(request?.id.toString());
    }
    await queryClient.invalidateQueries({
      queryKey: QueryKeyFactory.module.cx.serviceRequests.details(
        String(request?.id),
      ),
    });
  };

  const { serviceOrders } = useServiceOrders(
    {
      remote_utility_id: Number(getRemoteUtilityId()),
      request_type: "service-order",
      request_id: String(request?.id ?? "NA"),
    },
    { enabled: String(request?.id) !== undefined },
  );

  const hasApprovedOrInProgressSOP =
    serviceOrders?.some((serviceOrder) =>
      [
        "Created",
        "Not Assigned",
        "In-Progress",
        "Assigned",
        "Overdue",
        "Completed",
        "Accepted",
        "Refused",
        "Incomplete",
      ].includes(serviceOrder?.statusDisplay),
    ) || false;

  const isFinalState =
    request?.statusDisplay?.toLowerCase() === "completed" ||
    request?.statusDisplay?.toLowerCase() === "rejected";

  const serviceDetails = [
    { label: "Service Id", value: request?.requestNo ?? "-" },
    {
      label: "Service Name",
      value: request?.utilitySupportRequest?.name ?? "-",
    },
    {
      label: "Code",
      value: request?.utilitySupportRequest?.configurationCode ?? "-",
    },
    { label: "Priority", value: request?.priorityLevelDisplay ?? "-" },
    {
      label: "Utility Service",
      value: request?.utilitySupportRequest?.productCode?.length ? (
        <div className="flex gap-2 flex-wrap mt-1">
          {request.utilitySupportRequest.productCode.map((code: string) => (
            <Badge key={code} variant="outline" className=" bg-white">
              {code}
            </Badge>
          ))}
        </div>
      ) : (
        "-"
      ),
    },
    { label: "Category", value: request?.categoryDisplay ?? "-" },
    { label: "Sub Category", value: request?.subCategoryDisplay ?? "-" },
    {
      label: "Service Fees",
      value: `${currency} ${request?.utilitySupportRequest?.extraData?.serviceCharge ?? "0"}`,
    },
    {
      label: "Service Payment",
      value: request?.additionalData?.transactionStatusDisplay,
    },
    {
      label: "Preferred Date",
      value: request?.additionalData?.scheduleDate
        ? new Date(request.additionalData.scheduleDate).toLocaleDateString(
            "en-GB",
          )
        : "-",
    },
    {
      label: "Preferred Time Slot",
      value: request?.additionalData?.preferredTimeSlot ?? "-",
    },
    {
      label: "Field Work Required",
      value: request?.utilitySupportRequest?.extraData?.fieldForceRequired
        ? "Yes"
        : "No",
    },
    {
      label: "Description",
      value: request?.utilitySupportRequest?.longDescription ?? "NA",
      className: "col-span-2 md:col-span-4",
    },
  ];

  const slaTargets = [
    {
      label: "Acknowledgement",
      value: request?.utilitySupportRequest?.extraData?.acknowlegmentTime,
      tooltip: "Target time taken to acknowledge a request after it is raised.",
    },
    {
      label: "Response",
      value: request?.utilitySupportRequest?.extraData?.responseTime,
      tooltip: "Target time taken to give the first response to a request.",
    },
    {
      label: "Resolution",
      value: request?.utilitySupportRequest?.extraData?.resolutionTime,
      tooltip: "Target time taken to fully resolve and close the request.",
    },
  ];

  const slaAchieved = [
    {
      label: "Acknowledgement",
      value: request?.acknowledgementTimeAchieved ?? "Pending",
      tooltip: "Actual time taken to acknowledge a request after it is raised.",
    },
    {
      label: "Response",
      value: request?.responseTime ?? "Pending",
      tooltip: "Actual time taken to give the first response to a request.",
    },
    {
      label: "Resolution",
      value: request?.resolutionTimeAchieved ?? "Pending",
      tooltip: "Actual time taken to fully resolve and close the request.",
    },
  ];

  const [isPaymentDetailsModalOpen, setIsPaymentDetailsModalOpen] =
    useState(false);

  return (
    <div className="space-y-6">
      {/* Service Request Details */}
      <div className="space-y-3">
        <h4 className="card-heading-text flex items-center gap-2">
          Service Request Details
        </h4>
        <div className="space-y-4 p-4 border border-gray-200 rounded-md bg-white">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {serviceDetails.map((detail, index) => (
              <ValueLabel
                key={index}
                label={detail.label}
                value={detail.value}
                className={detail.className}
              />
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-md">
            {/* SLA Target */}
            <div className="border border-gray-200 p-4 rounded-md min-w-0">
              <p className="card-heading-text mb-4">SLA Target</p>
              <div className="grid grid-cols-3 w-full gap-2 sm:gap-3 min-w-0">
                {slaTargets.map((sla, index) => (
                  <ValueLabel
                    key={index}
                    label={sla.label}
                    value={sla.value}
                    tooltip={sla.tooltip}
                    className="min-w-0 [&_p.text-xs]:text-[11px] [&_p.text-xs]:tracking-normal"
                  />
                ))}
              </div>
            </div>

            {/* SLA Achieved */}
            <div className="border border-gray-200 p-4 rounded-md min-w-0">
              <p className="card-heading-text mb-4">SLA Achieved</p>
              <div className="grid grid-cols-3 w-full gap-2 sm:gap-3 min-w-0">
                {slaAchieved.map((sla, index) => (
                  <ValueLabel
                    key={index}
                    label={sla.label}
                    value={sla.value}
                    tooltip={sla.tooltip}
                    className={cn(
                      "min-w-0 [&_p.text-xs]:text-[11px] [&_p.text-xs]:tracking-normal",
                      sla.value !== "Pending" ? "text-green-600" : "",
                    )}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="border-l-4 border-black-400 p-2 rounded mt-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-4 w-4" />
              </div>
              <div className="ml-2">
                <p className="text-xs">
                  {request?.slaSummary.slaDueDate &&
                  new Date(request?.slaSummary.slaDueDate) > new Date()
                    ? `This request is due in ${Math.ceil(
                        (new Date(request?.slaSummary.slaDueDate).getTime() -
                          new Date().getTime()) /
                          (1000 * 60 * 60 * 24),
                      )} days.`
                    : "Please handle this request promptly."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Payments Section */}
      {request?.additionalData.transactionStatusDisplay === "Paid" ? (
        <div className="p-4 border border-green-200 bg-green-50/30 rounded-md">
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-green-100">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-100 text-green-600">
                <CheckCircle className="h-5 w-5" />
              </div>
              <ValueLabel
                label="Payment Completed"
                value="The service fee has been successfully paid. Field work can now proceed as scheduled."
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-9 border-green-200 hover:bg-green-100 text-green-700 transition-all font-medium"
              onClick={() => setIsPaymentDetailsModalOpen(true)}
            >
              View Payment Details
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 px-2">
            <ValueLabel
              label="Amount Paid"
              value={
                request?.paymentData?.amount
                  ? `${currency} ${Number(request.paymentData.amount).toFixed(2)}`
                  : "NA"
              }
            />
            <ValueLabel
              label="Payment Date"
              value={request?.paymentData?.paymentDate ?? "NA"}
            />
            <ValueLabel
              label="Payment Method"
              value={request?.paymentModeDisplay ?? "N/A"}
            />
            <ValueLabel
              label="Transaction ID"
              value={request?.paymentData?.transactionId ?? "N/A"}
            />
          </div>
        </div>
      ) : (
        <div className="p-4 border border-red-200 bg-red-50/30 rounded-md">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1">
              <div className="p-2 rounded-full bg-red-100 text-red-600 shrink-0">
                <AlertCircle className="h-5 w-5" />
              </div>
              <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-8 flex-1">
                <ValueLabel
                  label="Payment Required"
                  value="The service fee for this request has not been paid. Payment must be completed before field work can proceed."
                />
                <div className="shrink-0">
                  <p className="text-xs text-gray-500 font-medium">
                    Amount to be Paid
                  </p>
                  <p className="text-lg font-bold text-red-600">
                    {request?.utilitySupportRequest?.extraData?.serviceCharge
                      ? `${currency} ${Number(request.utilitySupportRequest.extraData.serviceCharge).toFixed(2)}`
                      : "NA"}
                  </p>
                </div>
              </div>
            </div>
            <Button
              size="sm"
              disabled={isServiceCompleted}
              className={cn(
                "bg-red-600 hover:bg-red-700 h-9 px-8 transition-all shadow-sm shrink-0",
                isServiceCompleted &&
                  "opacity-50 cursor-not-allowed hover:bg-red-600 pointer-events-none",
              )}
              onClick={() =>
                !isServiceCompleted &&
                navigate(
                  `/cx/payments/create?consumerId=${request?.consumer?.id}&search=${request?.consumer?.consumerNo}`,
                  {
                    state: {
                      source: "serviceDetails",
                      search: request?.consumer?.consumerNo,
                      paymentType: "5",
                      serviceId: request?.id,
                    },
                  },
                )
              }
            >
              Pay Now
            </Button>
          </div>
        </div>
      )}

      {/* Service Location */}
      <div className="space-y-3">
        <h4 className="card-heading-text flex items-center gap-2">
          Service Location
        </h4>
        <div className="space-y-4 p-4 border border-gray-200 rounded-md bg-white">
          <div className="grid grid-cols-1 gap-6">
            <ValueLabel
              label="Service Address"
              value={
                request?.consumer?.consumerAddress
                  ? formatTerritoryAddress(
                      {
                        service:
                          request.consumer.consumerAddress.service
                            .hierarchyNames,
                      },
                      "service",
                    )
                  : "NA"
              }
            />
          </div>
        </div>
      </div>

      {/* Additional Notes */}
      <div className="space-y-3">
        <h4 className="card-heading-text flex items-center gap-2">
          Additional Notes
        </h4>
        <div className="space-y-4 p-4 border border-gray-200 rounded-md bg-white">
          <div className="grid grid-cols-1 gap-6">
            <ValueLabel
              label="Notes"
              value={
                request?.additionalData?.additionalInstruction ||
                "No additional notes provided"
              }
            />
          </div>
        </div>
      </div>

      {/* Closure Requirements & Actions */}
      <div className="space-y-3">
        <h4 className="card-heading-text flex items-center gap-2">
          Closure Requirements & Actions
        </h4>
        <div className="space-y-4 p-4 border border-gray-200 rounded-md bg-white">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="serviceCompletion"
                  className="mt-1"
                  checked={closureRequirements?.serviceCompletion}
                  disabled={isFinalState}
                  onCheckedChange={(checked) =>
                    handleClosureRequirementChange(
                      "serviceCompletion",
                      checked as boolean,
                    )
                  }
                />
                <div
                  className={`grid gap-1.5 leading-none ${isFinalState ? "cursor-not-allowed" : "cursor-pointer"}`}
                  onClick={() =>
                    !isFinalState &&
                    handleClosureRequirementChange(
                      "serviceCompletion",
                      !closureRequirements?.serviceCompletion,
                    )
                  }
                >
                  <label
                    htmlFor="serviceCompletion"
                    className="text-sm font-semibold leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Field Work Completed
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Confirm that all on-site field work has been carried out as
                    per the service request specifications.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="customerNotification"
                  className="mt-1"
                  checked={closureRequirements?.customerNotification}
                  disabled={isFinalState}
                  onCheckedChange={(checked) =>
                    handleClosureRequirementChange(
                      "customerNotification",
                      checked as boolean,
                    )
                  }
                />
                <div
                  className={`grid gap-1.5 leading-none ${isFinalState ? "cursor-not-allowed" : "cursor-pointer"}`}
                  onClick={() =>
                    !isFinalState &&
                    handleClosureRequirementChange(
                      "customerNotification",
                      !closureRequirements?.customerNotification,
                    )
                  }
                >
                  <label
                    htmlFor="customerNotification"
                    className="text-sm font-semibold leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Customer Confirmation
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Verify that the customer has acknowledged the service
                    delivery and is satisfied with the outcome.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="qualityCheck"
                  className="mt-1"
                  checked={closureRequirements?.qualityCheck}
                  disabled={isFinalState}
                  onCheckedChange={(checked) =>
                    handleClosureRequirementChange(
                      "qualityCheck",
                      checked as boolean,
                    )
                  }
                />
                <div
                  className={`grid gap-1.5 leading-none ${isFinalState ? "cursor-not-allowed" : "cursor-pointer"}`}
                  onClick={() =>
                    !isFinalState &&
                    handleClosureRequirementChange(
                      "qualityCheck",
                      !closureRequirements?.qualityCheck,
                    )
                  }
                >
                  <label
                    htmlFor="qualityCheck"
                    className="text-sm font-semibold leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Inspection Passed
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Ensure a post-service inspection has been completed and
                    meets all safety and quality standards.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="documentationUpload"
                  className="mt-1"
                  checked={closureRequirements?.documentationUpload}
                  disabled={isFinalState}
                  onCheckedChange={(checked) =>
                    handleClosureRequirementChange(
                      "documentationUpload",
                      checked as boolean,
                    )
                  }
                />
                <div
                  className={`grid gap-1.5 leading-none ${isFinalState ? "cursor-not-allowed" : "cursor-pointer"}`}
                  onClick={() =>
                    !isFinalState &&
                    handleClosureRequirementChange(
                      "documentationUpload",
                      !closureRequirements?.documentationUpload,
                    )
                  }
                >
                  <label
                    htmlFor="documentationUpload"
                    className="text-sm font-semibold leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Documentation Submitted
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Confirm that all required reports, photos, and service logs
                    have been uploaded and filed correctly.
                  </p>
                </div>
              </div>
            </div>

            <div className="border-l-4 border-black-400 p-3 bg-gray-50 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Info className="h-4 w-4 text-gray-600" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-700">
                    Please ensure all selected requirements are met before
                    marking this request as resolved.
                  </p>
                </div>
              </div>
            </div>

            {request?.statusDisplay?.toLowerCase() !== "completed" &&
              request?.statusDisplay?.toLowerCase() !== "rejected" && (
                <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
                  <Button
                    variant="outline"
                    className="border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600"
                    onClick={handleRejectRequest}
                    disabled={hasApprovedOrInProgressSOP}
                  >
                    Reject Request
                  </Button>
                  <Button
                    className="bg-green-600 hover:bg-green-700 text-white disabled:bg-green-200"
                    onClick={handleResolveRequest}
                    isLoading={isResolving}
                    disabled={
                      !closureRequirements?.customerNotification ||
                      !closureRequirements?.serviceCompletion ||
                      !closureRequirements?.qualityCheck ||
                      !closureRequirements?.documentationUpload ||
                      hasApprovedOrInProgressSOP
                    }
                  >
                    Mark as Resolved
                  </Button>
                </div>
              )}
          </div>
        </div>
      </div>

      <PaymentDetailsModal
        isOpen={isPaymentDetailsModalOpen}
        onClose={() => setIsPaymentDetailsModalOpen(false)}
        payment={request?.paymentData}
      />
    </div>
  );
};

export default ServiceDetailsTab;
